import dayjs from "dayjs";

/**
 * Every audit search covers at most 90 calendar days, both ends included
 * (EN-5288). Older history is reached by moving the window back.
 */
export const MAX_AUDIT_WINDOW_DAYS = 90;

/** A window as the date fields hold it: local `YYYY-MM-DD` days. */
export interface IAuditLogWindow {
  startDate: string;
  endDate: string;
}

const DATE_VALUE = "YYYY-MM-DD";

/** The latest full window: the last 90 days, today included. */
export function latestAuditWindow(): IAuditLogWindow {
  const today = dayjs();
  return {
    startDate: today
      .subtract(MAX_AUDIT_WINDOW_DAYS - 1, "day")
      .format(DATE_VALUE),
    endDate: today.format(DATE_VALUE),
  };
}

/**
 * The 90 days just before (`-1`) or after (`1`) the window. Moving forward
 * stops at today.
 */
export function shiftAuditWindow(
  window: IAuditLogWindow,
  direction: -1 | 1
): IAuditLogWindow {
  const today = dayjs().startOf("day");
  const later = dayjs(window.endDate).add(MAX_AUDIT_WINDOW_DAYS, "day");
  let end = later.isAfter(today) ? today : later;
  if (direction < 0) end = dayjs(window.startDate).subtract(1, "day");
  return {
    startDate: end
      .subtract(MAX_AUDIT_WINDOW_DAYS - 1, "day")
      .format(DATE_VALUE),
    endDate: end.format(DATE_VALUE),
  };
}

/** True when the window already reaches today, so there is nothing newer. */
export function isLatestAuditWindow(window: IAuditLogWindow): boolean {
  return !dayjs(window.endDate).isBefore(dayjs(), "day");
}

/** Why the window can't be searched, or null when it can. */
export function auditWindowError(window: IAuditLogWindow): string | null {
  const { startDate, endDate } = window;
  if (!startDate || !endDate) return "Choose both dates.";
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  if (end.isBefore(start)) return "The end date is before the start date.";
  if (end.diff(start, "day") + 1 > MAX_AUDIT_WINDOW_DAYS) {
    return `Search at most ${MAX_AUDIT_WINDOW_DAYS} days at a time. For older history, move both dates back.`;
  }
  return null;
}

/** The window as the API takes it: local start of the first day to end of the last, in ISO. */
export function auditWindowToRange(window: IAuditLogWindow) {
  return {
    startDate: dayjs(window.startDate).startOf("day").toISOString(),
    endDate: dayjs(window.endDate).endOf("day").toISOString(),
  };
}

/** Every operation but READ: views are logged on each open and bury the changes. */
export const CHANGE_OPERATIONS = ["INSERT", "UPDATE", "DELETE"];

/** What the History tabs call each operation. */
export const OPERATION_LABELS: Record<string, string> = {
  INSERT: "Created",
  UPDATE: "Changed",
  DELETE: "Deleted",
  READ: "Viewed",
};
